import React from 'react';

import { Box, Button, Group, Paper, Stack, Tabs, Text, ThemeIcon, Title } from '@mantine/core';
import { IconBolt } from '@tabler/icons-react';
import KiloAlert from 'components/KiloAlert';
import PageHeader from 'components/PageHeader';
import { useAuth } from 'context/auth-context';
import { useNavigate, useSearchParams } from 'react-router-dom';

import GLMKiloFlowTableContainer from './components/GLMKiloFlowTableContainer';
import KiloGLMFlowTableContainer from './components/KiloGLMFlowTableContainer';

const GLMIntegrationDisabled = (): JSX.Element => (
  <Stack p='xl' spacing='xs' align='center'>
    <ThemeIcon radius='xl' size={72} variant='light' color='kiloBlue'>
      <IconBolt size={36} />
    </ThemeIcon>
    <Title order={2} mt='md'>
      Gym Lead Machine is not enabled
    </Title>
    <Text color='dimmed' align='center' maw={460}>
      To create automations between Kilo and Gym Lead Machine, please contact support to enable this
      integration.
    </Text>
  </Stack>
);

const GLMFlows = (): JSX.Element => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const isDemoGym = !!session?.demoGym;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'glm-to-kilo' ? 'glm-to-kilo' : 'kilo-to-glm';
  const setActiveTab = (value: string | null): void => {
    setSearchParams(value ? { tab: value } : {});
  };

  if (!session?.gym?.ghlV2Enabled && !isDemoGym) {
    return (
      <>
        <PageHeader>
          <Group position='apart' align='center' noWrap>
            <Title order={1} size='h1'>
              Integration
            </Title>
          </Group>
        </PageHeader>
        <GLMIntegrationDisabled />
      </>
    );
  }

  return (
    <>
      <Paper shadow='xl' radius='md' withBorder py='sm' px={0} style={{ minWidth: 'fit-content', flex: 1 }}>
        <PageHeader>
          <Group position='apart' align='center' noWrap>
            <Title order={1} size='h1'>
              Gym Lead Machine Integration
            </Title>
            <Group>
              <Button
                color='kiloBlue'
                onClick={() =>
                  navigate(
                    activeTab === 'kilo-to-glm'
                      ? '/integrations/glm/builder'
                      : '/integrations/glm/glm-builder',
                  )
                }
              >
                New automation
              </Button>
            </Group>
          </Group>
        </PageHeader>
        {isDemoGym && (
          <Box px='md' pb='sm'>
            <KiloAlert variantStyle='warning'>
              This is a simulated Gym Lead Machine integration for demo purposes only. It is not connected to
              a real account, and any triggers you create here won&apos;t sync anywhere.
            </KiloAlert>
          </Box>
        )}
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List px='md'>
            <Tabs.Tab value='kilo-to-glm'>Kilo to GLM</Tabs.Tab>
            <Tabs.Tab value='glm-to-kilo'>GLM to Kilo</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value='kilo-to-glm'>
            <KiloGLMFlowTableContainer />
          </Tabs.Panel>
          <Tabs.Panel value='glm-to-kilo'>
            <GLMKiloFlowTableContainer />
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </>
  );
};

export default GLMFlows;
